/**
 * Dialog360Service
 * Full 360dialog WhatsApp management surface.
 * Route: api/dialog360
 *
 * Separate from the legacy WhatsAppService (api/whatsapp). Every send-now
 * endpoint returns the persisted AppWhatsAppMessage; templates/media/profile
 * endpoints return the corresponding 360dialog response DTOs. All response
 * bodies are wrapped in ResponseData<T>; each method has an unwrapped variant
 * plus a *Raw variant that returns the full envelope.
 */
import { BaseService } from '../base/BaseService';
/**
 * Dialog360Service
 * Handles the api/dialog360/* WhatsApp management endpoints.
 */
export class Dialog360Service extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? Dialog360Service.DEFAULT_SERVICE_PATH,
            basePath: Dialog360Service.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== SEND OPERATIONS ====================
    /**
     * Send a text message
     * POST api/dialog360/send/text
     */
    async sendText(request) {
        const response = await this.sendTextRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send text message');
        }
        return response.data;
    }
    /** Send text message with full response */
    sendTextRaw(request) {
        const endpoint = this.getEndpoint('send-text', 'send/text');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a template message
     * POST api/dialog360/send/template
     */
    async sendTemplate(request) {
        const response = await this.sendTemplateRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send template message');
        }
        return response.data;
    }
    /** Send template message with full response */
    sendTemplateRaw(request) {
        const endpoint = this.getEndpoint('send-template', 'send/template');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send an image message
     * POST api/dialog360/send/image
     */
    async sendImage(request) {
        const response = await this.sendImageRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send image message');
        }
        return response.data;
    }
    /** Send image message with full response */
    sendImageRaw(request) {
        const endpoint = this.getEndpoint('send-image', 'send/image');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a video message
     * POST api/dialog360/send/video
     */
    async sendVideo(request) {
        const response = await this.sendVideoRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send video message');
        }
        return response.data;
    }
    /** Send video message with full response */
    sendVideoRaw(request) {
        const endpoint = this.getEndpoint('send-video', 'send/video');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send an audio message
     * POST api/dialog360/send/audio
     */
    async sendAudio(request) {
        const response = await this.sendAudioRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send audio message');
        }
        return response.data;
    }
    /** Send audio message with full response */
    sendAudioRaw(request) {
        const endpoint = this.getEndpoint('send-audio', 'send/audio');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a document message
     * POST api/dialog360/send/document
     */
    async sendDocument(request) {
        const response = await this.sendDocumentRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send document message');
        }
        return response.data;
    }
    /** Send document message with full response */
    sendDocumentRaw(request) {
        const endpoint = this.getEndpoint('send-document', 'send/document');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a sticker message
     * POST api/dialog360/send/sticker
     */
    async sendSticker(request) {
        const response = await this.sendStickerRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send sticker message');
        }
        return response.data;
    }
    /** Send sticker message with full response */
    sendStickerRaw(request) {
        const endpoint = this.getEndpoint('send-sticker', 'send/sticker');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a location message
     * POST api/dialog360/send/location
     */
    async sendLocation(request) {
        const response = await this.sendLocationRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send location message');
        }
        return response.data;
    }
    /** Send location message with full response */
    sendLocationRaw(request) {
        const endpoint = this.getEndpoint('send-location', 'send/location');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a contacts message
     * POST api/dialog360/send/contacts
     */
    async sendContacts(request) {
        const response = await this.sendContactsRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send contacts message');
        }
        return response.data;
    }
    /** Send contacts message with full response */
    sendContactsRaw(request) {
        const endpoint = this.getEndpoint('send-contacts', 'send/contacts');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send an interactive buttons message
     * POST api/dialog360/send/interactive/buttons
     */
    async sendInteractiveButtons(request) {
        const response = await this.sendInteractiveButtonsRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send interactive buttons message');
        }
        return response.data;
    }
    /** Send interactive buttons message with full response */
    sendInteractiveButtonsRaw(request) {
        const endpoint = this.getEndpoint('send-interactive-buttons', 'send/interactive/buttons');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send an interactive list message
     * POST api/dialog360/send/interactive/list
     */
    async sendInteractiveList(request) {
        const response = await this.sendInteractiveListRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send interactive list message');
        }
        return response.data;
    }
    /** Send interactive list message with full response */
    sendInteractiveListRaw(request) {
        const endpoint = this.getEndpoint('send-interactive-list', 'send/interactive/list');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a call-to-action URL message
     * POST api/dialog360/send/interactive/cta-url
     */
    async sendCtaUrl(request) {
        const response = await this.sendCtaUrlRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send CTA URL message');
        }
        return response.data;
    }
    /** Send CTA URL message with full response */
    sendCtaUrlRaw(request) {
        const endpoint = this.getEndpoint('send-cta-url', 'send/interactive/cta-url');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a flow message
     * POST api/dialog360/send/interactive/flow
     */
    async sendFlow(request) {
        const response = await this.sendFlowRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send flow message');
        }
        return response.data;
    }
    /** Send flow message with full response */
    sendFlowRaw(request) {
        const endpoint = this.getEndpoint('send-flow', 'send/interactive/flow');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a reaction
     * POST api/dialog360/send/reaction
     */
    async sendReaction(request) {
        const response = await this.sendReactionRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send reaction');
        }
        return response.data;
    }
    /** Send reaction with full response */
    sendReactionRaw(request) {
        const endpoint = this.getEndpoint('send-reaction', 'send/reaction');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Send a raw 360dialog message envelope
     * POST api/dialog360/send/raw
     */
    async sendRaw(request) {
        const response = await this.sendRawRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to send raw message');
        }
        return response.data;
    }
    /** Send raw message with full response */
    sendRawRaw(request) {
        const endpoint = this.getEndpoint('send-raw', 'send/raw');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Mark a message as read (optionally with typing indicator)
     * POST api/dialog360/send/mark-read/{messageId}?typing={typing}
     */
    async markRead(messageId, typing = false) {
        const response = await this.markReadRaw(messageId, typing);
        return response.success;
    }
    /** Mark message as read with full response */
    markReadRaw(messageId, typing = false) {
        const endpoint = this.getEndpoint('mark-read', `send/mark-read/${encodeURIComponent(messageId)}`);
        const url = this.buildUrlWithParams(endpoint, { typing });
        return this.http.post(url, {});
    }
    /**
     * Queue a WhatsApp message for async delivery via the worker pipeline
     * POST api/dialog360/send/queue
     */
    async queue(request) {
        const response = await this.queueRaw(request);
        return response.success;
    }
    /** Queue message with full response (no data payload) */
    queueRaw(request) {
        const endpoint = this.getEndpoint('send-queue', 'send/queue');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    // ==================== TEMPLATE OPERATIONS ====================
    /**
     * List message templates
     * GET api/dialog360/templates?limit=&offset=&sort=
     */
    async listTemplates(limit = 1000, offset = 0, sort = 'name') {
        const response = await this.listTemplatesRaw(limit, offset, sort);
        return response.data ?? null;
    }
    /** List templates with full response */
    listTemplatesRaw(limit = 1000, offset = 0, sort = 'name') {
        const endpoint = this.getEndpoint('templates', 'templates');
        const url = this.buildUrlWithParams(endpoint, { limit, offset, sort });
        return this.http.get(url);
    }
    /**
     * Create a message template
     * POST api/dialog360/templates
     */
    async createTemplate(request) {
        const response = await this.createTemplateRaw(request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create template');
        }
        return response.data;
    }
    /** Create template with full response */
    createTemplateRaw(request) {
        const endpoint = this.getEndpoint('template-create', 'templates');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Edit a message template by external id
     * POST api/dialog360/templates/{externalId}
     */
    async editTemplate(externalId, request) {
        const response = await this.editTemplateRaw(externalId, request);
        if (!response.data) {
            throw new Error(response.message || 'Failed to edit template');
        }
        return response.data;
    }
    /** Edit template with full response */
    editTemplateRaw(externalId, request) {
        const endpoint = this.getEndpoint('template-edit', `templates/${encodeURIComponent(externalId)}`);
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Delete a message template by name
     * DELETE api/dialog360/templates/{name}
     */
    async deleteTemplate(name) {
        const response = await this.deleteTemplateRaw(name);
        if (!response.data) {
            throw new Error(response.message || 'Failed to delete template');
        }
        return response.data;
    }
    /** Delete template with full response */
    deleteTemplateRaw(name) {
        const endpoint = this.getEndpoint('template-delete', `templates/${encodeURIComponent(name)}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== MEDIA OPERATIONS ====================
    /**
     * Upload media to 360dialog
     * POST api/dialog360/media (multipart form: file + messaging_product=whatsapp)
     * @param file The media content (File or Blob)
     * @param mimeType Optional content type (used when wrapping a Blob)
     * @param filename Optional filename (used when wrapping a Blob)
     */
    async uploadMedia(file, mimeType, filename) {
        const response = await this.uploadMediaRaw(file, mimeType, filename);
        if (!response.data) {
            throw new Error(response.message || 'Failed to upload media');
        }
        return response.data;
    }
    /** Upload media with full response */
    uploadMediaRaw(file, mimeType, filename) {
        const endpoint = this.getEndpoint('media-upload', 'media');
        const url = this.buildUrl(endpoint);
        const formData = new FormData();
        const name = filename ?? (file instanceof File ? file.name : 'upload.bin');
        if (mimeType && !(file instanceof File)) {
            formData.append('file', new Blob([file], { type: mimeType }), name);
        }
        else {
            formData.append('file', file, name);
        }
        formData.append('messaging_product', 'whatsapp');
        return this.http.postFormData(url, formData);
    }
    /**
     * Retrieve media metadata
     * GET api/dialog360/media/{id}
     */
    async getMedia(id) {
        const response = await this.getMediaRaw(id);
        if (!response.data) {
            throw new Error(`Media ${id} not found`);
        }
        return response.data;
    }
    /** Get media metadata with full response */
    getMediaRaw(id) {
        const endpoint = this.getEndpoint('media-get', `media/${encodeURIComponent(id)}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Download media content as a Blob (bypasses the ResponseData envelope)
     * GET api/dialog360/media/{id}/download
     */
    downloadMedia(id) {
        const endpoint = this.getEndpoint('media-download', `media/${encodeURIComponent(id)}/download`);
        const url = this.buildUrl(endpoint);
        return this.http.getBlob(url);
    }
    /**
     * Delete media
     * DELETE api/dialog360/media/{id}
     */
    async deleteMedia(id) {
        const response = await this.deleteMediaRaw(id);
        if (!response.data) {
            throw new Error(response.message || 'Failed to delete media');
        }
        return response.data;
    }
    /** Delete media with full response */
    deleteMediaRaw(id) {
        const endpoint = this.getEndpoint('media-delete', `media/${encodeURIComponent(id)}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== PROFILE OPERATIONS ====================
    /**
     * Get the WhatsApp business profile
     * GET api/dialog360/profile
     */
    async getProfile() {
        const response = await this.getProfileRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to get business profile');
        }
        return response.data;
    }
    /** Get profile with full response */
    getProfileRaw() {
        const endpoint = this.getEndpoint('profile', 'profile');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Update the WhatsApp business profile
     * PUT api/dialog360/profile
     */
    async updateProfile(profile) {
        const response = await this.updateProfileRaw(profile);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update business profile');
        }
        return response.data;
    }
    /** Update profile with full response */
    updateProfileRaw(profile) {
        const endpoint = this.getEndpoint('profile-update', 'profile');
        const url = this.buildUrl(endpoint);
        return this.http.put(url, profile);
    }
    /**
     * Get the phone numbers registered on the WABA
     * GET api/dialog360/profile/phone-numbers
     */
    async getPhoneNumbers() {
        const response = await this.getPhoneNumbersRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to get phone numbers');
        }
        return response.data;
    }
    /** Get phone numbers with full response */
    getPhoneNumbersRaw() {
        const endpoint = this.getEndpoint('phone-numbers', 'profile/phone-numbers');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== DIAGNOSTICS ====================
    /**
     * Lightweight credential/connectivity probe (sends no message)
     * GET api/dialog360/diagnostics/test-connection
     */
    async testConnection() {
        const response = await this.testConnectionRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to test connection');
        }
        return response.data;
    }
    /** Test connection with full response */
    testConnectionRaw() {
        const endpoint = this.getEndpoint('test-connection', 'diagnostics/test-connection');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
Dialog360Service.DEFAULT_SERVICE_PATH = 'communications';
Dialog360Service.BASE_PATH = 'api/dialog360';
//# sourceMappingURL=Dialog360Service.js.map